import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { LMSCourse } from '../entities/LMSCourse.js';
import { LMSCourseEnrollment } from '../entities/LMSCourseEnrollment.js';

export const subscribeScopedCoursesPaginated: ReturnType<typeof registerSubscription> = registerSubscription({
  operationName: 'subscribeScopedCoursesPaginated',
  type: () => [LMSCourse],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve: (skip: number, limit: number, filter?: AdornisFilter) => (gqlFields: BaseQLSelectionSet<LMSCourse>) => {
    const userFilter = filter?.getMongoFilter() ?? {};
    // Hide soft-deleted courses (deletedAt == null matches both null and missing).
    const combinedFilter = Object.keys(userFilter).length
      ? { $and: [{ deletedAt: null }, userFilter] }
      : { deletedAt: null };
    return watchQuery<LMSCourse>(LMSCourse._collectionName, combinedFilter);
  },
});

// Total courses matching the current filter (after hiding soft-deleted). Mirrors the filter of
// `subscribeScopedCoursesPaginated` so the list header count equals the list's row count.
export const countScopedCourses = registerQuery({
  operationName: 'countScopedCourses',
  type: () => Int,
  params: [{ name: 'filter', type: () => AdornisFilter }],
  resolve: (filter?: AdornisFilter) => async (): Promise<number> => {
    const userFilter = filter?.getMongoFilter() ?? {};
    const combinedFilter = Object.keys(userFilter).length
      ? { $and: [{ deletedAt: null }, userFilter] }
      : { deletedAt: null };
    const coll = await getRawCollection<LMSCourse>(LMSCourse._collectionName);
    return coll.countDocuments(combinedFilter);
  },
});

export const subscribeScopedEnrollmentsPaginated: ReturnType<typeof registerSubscription> = registerSubscription({
  operationName: 'subscribeScopedEnrollmentsPaginated',
  type: () => [LMSCourseEnrollment],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve:
    (skip: number, limit: number, filter?: AdornisFilter) => (gqlFields: BaseQLSelectionSet<LMSCourseEnrollment>) => {
      const userFilter = filter?.getMongoFilter() ?? {};
      return watchQuery<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName, userFilter);
    },
});
